import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
    fetchMistakeNotes,
    updateMistakeNote,
    deleteMistakeNote,
    fetchMistakeStats,
} from "../../services/MistakeNoteService";
import { MATH_TOPICS, ERROR_TYPES } from "../../constants/learningModes";

/**
 * MistakeNotebook — Modal sổ tay lỗi sai.
 */
function MistakeNotebook({ open, onClose, onPractice }) {
    const [notes, setNotes] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filterTopic, setFilterTopic] = useState("all");
    const [filterError, setFilterError] = useState("all");
    const [filterMastered, setFilterMastered] = useState("all");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const loadNotes = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchMistakeNotes({
                page,
                limit: 10,
                topic: filterTopic,
                errorType: filterError,
                mastered: filterMastered,
            });
            setNotes(res?.data || []);
            setTotalPages(res?.totalPages || 1);
        } catch (e) {
            console.error("loadNotes:", e);
        } finally {
            setLoading(false);
        }
    }, [page, filterTopic, filterError, filterMastered]);

    const loadStats = useCallback(async () => {
        try {
            const res = await fetchMistakeStats();
            setStats(res);
        } catch (e) {
            console.error("loadStats:", e);
        }
    }, []);

    useEffect(() => {
        if (open) {
            loadNotes();
            loadStats();
        }
    }, [open, loadNotes, loadStats]);

    const handleToggleMastered = async (note) => {
        try {
            await updateMistakeNote(note._id, { mastered: !note.mastered });
            toast.success(note.mastered ? "Đã bỏ đánh dấu nắm vững" : "Đã đánh dấu nắm vững!");
            loadNotes();
            loadStats();
        } catch (e) {
            toast.error("Lỗi cập nhật");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Xóa lỗi sai này?")) return;
        try {
            await deleteMistakeNote(id);
            toast.success("Đã xóa");
            loadNotes();
            loadStats();
        } catch (e) {
            toast.error("Lỗi xóa");
        }
    };

    const handlePractice = (note) => {
        const prompt = `Hãy tạo 3 bài tập giúp tôi luyện lại lỗi: "${note.errorType}" trong chủ đề "${note.topic}". Lỗi cụ thể: ${note.wrongStep || note.explanation}. Mỗi bài có lời giải chi tiết.`;
        onPractice?.(prompt);
        onClose();
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                style={{ animation: "pimSlideUp 0.15s ease-out" }}
            >
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <span className="text-lg">📓</span>
                        <h2 className="text-base font-bold text-gray-900">Sổ tay lỗi sai</h2>
                        {stats && (
                            <span className="text-xs text-gray-500 ml-2">
                                {stats.unmastered} chưa nắm / {stats.total} tổng
                            </span>
                        )}
                    </div>
                    <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Filters */}
                <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap gap-2 flex-shrink-0">
                    <select
                        value={filterTopic}
                        onChange={(e) => { setFilterTopic(e.target.value); setPage(1); }}
                        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-red-500"
                    >
                        <option value="all">Tất cả chủ đề</option>
                        {MATH_TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>

                    <select
                        value={filterError}
                        onChange={(e) => { setFilterError(e.target.value); setPage(1); }}
                        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-red-500"
                    >
                        <option value="all">Tất cả loại lỗi</option>
                        {ERROR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>

                    <select
                        value={filterMastered}
                        onChange={(e) => { setFilterMastered(e.target.value); setPage(1); }}
                        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-red-500"
                    >
                        <option value="all">Tất cả</option>
                        <option value="false">Chưa nắm vững</option>
                        <option value="true">Đã nắm vững</option>
                    </select>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-5 py-3 min-h-0">
                    {loading ? (
                        <div className="text-center py-8 text-gray-500 text-sm">Đang tải...</div>
                    ) : notes.length === 0 ? (
                        <div className="text-center py-8">
                            <span className="text-3xl">📝</span>
                            <p className="text-sm text-gray-500 mt-2">Chưa có lỗi sai nào được lưu.</p>
                            <p className="text-xs text-gray-400 mt-1">Sử dụng chế độ "Kiểm tra lời giải" để phát hiện và lưu lỗi sai.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {notes.map((note) => (
                                <div
                                    key={note._id}
                                    className={`p-3 rounded-xl border transition-colors ${note.mastered
                                            ? "border-green-200 bg-green-50/50"
                                            : "border-gray-200 bg-white hover:border-gray-300"
                                        }`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700">
                                                    {note.topic}
                                                </span>
                                                <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full bg-orange-100 text-orange-700">
                                                    {note.errorType}
                                                </span>
                                                {note.mastered && (
                                                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full bg-green-100 text-green-700">
                                                        ✓ Đã nắm vững
                                                    </span>
                                                )}
                                            </div>
                                            {note.wrongStep && (
                                                <p className="text-xs text-gray-800 mb-1"><strong>Lỗi:</strong> {note.wrongStep}</p>
                                            )}
                                            {note.explanation && (
                                                <p className="text-xs text-gray-600 mb-1"><strong>Vì sao:</strong> {note.explanation}</p>
                                            )}
                                            {note.correction && (
                                                <p className="text-xs text-gray-600"><strong>Sửa:</strong> {note.correction}</p>
                                            )}
                                            {note.reminder && (
                                                <p className="text-xs text-purple-600 mt-1 italic">💡 {note.reminder}</p>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-1 flex-shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => handleToggleMastered(note)}
                                                className={`px-2 py-1 text-[10px] font-medium rounded-md transition-colors ${note.mastered
                                                        ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                                        : "bg-green-100 text-green-700 hover:bg-green-200"
                                                    }`}
                                                title={note.mastered ? "Bỏ đánh dấu" : "Đánh dấu nắm vững"}
                                            >
                                                {note.mastered ? "Bỏ ✓" : "Nắm vững ✓"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handlePractice(note)}
                                                className="px-2 py-1 text-[10px] font-medium rounded-md bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                                                title="Luyện lại lỗi này"
                                            >
                                                Luyện lại
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(note._id)}
                                                className="px-2 py-1 text-[10px] font-medium rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                                title="Xóa"
                                            >
                                                Xóa
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-center gap-2 flex-shrink-0">
                        <button
                            type="button"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => p - 1)}
                            className="px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                        >
                            ← Trước
                        </button>
                        <span className="text-xs text-gray-500">{page} / {totalPages}</span>
                        <button
                            type="button"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                            className="px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                        >
                            Sau →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MistakeNotebook;
